import { api } from "@/shared/api/base";
import { getAudioPublicToken } from "@/shared/api/songs";
import { openExternalUrl } from "@/shared/lib/miniAppOpenLink";
import { isAnyMiniApp } from "@/shared/lib/miniApp";
import { toast } from "@/shared/ui/Toast";
/**
 * Скачивание аудио/минуса по ПРЯМОЙ ссылке из хранилища (не через стрим бэка).
 *
 * Бэк отдаёт короткий JSON `{ url }` — presigned-ссылку на объект в MinIO с
 * `Content-Disposition: attachment` (имя = название песни). Файл качается
 * напрямую из хранилища, бэк байты не проксирует.
 *   GET /api/v1/public/audios/{id}/download-url               — полный трек
 *   GET /api/v1/public/audios/{id}/instrumental/download-url  — минус
 *
 * ВЕБ: <a href={presignedUrl}> — `Content-Disposition: attachment` заставляет
 * браузер скачать файл с нужным именем даже cross-origin (атрибут `download`
 * для cross-origin игнорируется, но заголовок ответа важнее).
 * МИНИ-АПП, полный трек: открываем публичную страницу песни /song/<token> во
 * внешнем браузере (та же ссылка, что шлют боты) — надёжнее прямого скачивания
 * mp3, которое в вебвью редиректит/проигрывает. Фолбэк (и для минуса): отдаём
 * ссылку мосту TG/MAX (`openLink`) — внешний браузер сохранит по
 * `Content-Disposition`. В вебвью клик по `<a download>` навигирует, а не
 * качает — см. openExternalUrl.
 *
 * Запрос за ссылкой в `downloadAudio` идёт через `api` (интерсептор добавит
 * auth-заголовки initData/Bearer — ручки `/audios/{id}/…` owner-gated). Фолбэк
 * (ручка недоступна / нет audioId): прямая ссылка на CDN, как было.
 *
 * ВАЖНО: низкоуровневые хелперы `sanitizeFileName` / `triggerWebDownload`
 * экспортируются и используются ещё и АНОНИМНЫМИ публичными флоу (страница-
 * подарок `/s/:token` — там своя ручка по токену, без auth). Поэтому они не
 * должны ничего предполагать про авторизацию и владение аудио — им приходит
 * готовый href.
 */
/**
 * Текущий язык интерфейса из localStorage (тот же ключ, что у LanguageProvider).
 * downloadAudio вызывается без React-контекста, а прокидывать language через все
 * точки вызова (часть — в параллельных ветках) не хочется — читаем защищённо.
 */
function currentLanguage() {
    try {
        const saved = localStorage.getItem("language");
        if (saved === "en")
            return "en";
    }
    catch {
        /* localStorage недоступен — по умолчанию ru */
    }
    return "ru";
}
/** Название песни → безопасное имя файла (без служебных символов, ≤120). */
export function sanitizeFileName(name) {
    const cleaned = (name || "")
        .replace(/[\\/:*?"<>|]+/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    return (cleaned || "song").slice(0, 120);
}
/**
 * Ссылка ведёт на ЧУЖОЙ origin? blob:/data: считаем «своими» — они порождены
 * этим же документом, атрибут `download` для них работает.
 */
function isCrossOriginHref(href) {
    try {
        const url = new URL(href, window.location.href);
        if (url.protocol !== "http:" && url.protocol !== "https:")
            return false;
        return url.origin !== window.location.origin;
    }
    catch {
        return false;
    }
}
/**
 * Клик по невидимому якорю `<a download>` — низкоуровневый триггер.
 *
 * Для cross-origin href браузер ИГНОРИРУЕТ атрибут `download`: если у ответа
 * нет `Content-Disposition: attachment` (legacy-строки без `path` отдаются
 * голым CDN-URL), клик превращается в НАВИГАЦИЮ — вкладка уходит с текущей
 * страницы на mp3. На странице-подарке `/s/:token` это «теряет» сам подарок,
 * поэтому такие ссылки открываем в новой вкладке (`rel=noopener` — чтобы
 * чужая страница не получила доступ к `window.opener`). Для same-origin (в том
 * числе blob:) поведение прежнее: тихое скачивание в текущей вкладке.
 */
function triggerAnchorDownload(href, fileName) {
    const link = document.createElement("a");
    link.href = href;
    link.download = fileName;
    if (isCrossOriginHref(href)) {
        link.target = "_blank";
        link.rel = "noopener";
    }
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}
/**
 * Тихо скачать файл в обычном вебе — в «Загрузки», без окна «Сохранить как» и
 * без открытия/проигрывания. Тянем байты в blob и качаем через same-origin
 * object-URL: браузер сохраняет с нужным именем. Прямой якорь на cross-origin
 * presigned-URL так не всегда делает — для legacy-URL без
 * `Content-Disposition: attachment` он навигирует/спрашивает («Проводник»).
 * Если blob не удался (CORS не разрешён на хранилище / сеть) — фолбэк на прямой
 * якорь (прежнее поведение, без регресса). В мини-аппе НЕ вызывать — там сначала
 * openExternalUrl (см. вызовы ниже).
 */
export async function triggerWebDownload(href, fileName) {
    try {
        const res = await fetch(href);
        if (!res.ok)
            throw new Error(`download fetch failed: ${res.status}`);
        const blob = await res.blob();
        const objectUrl = URL.createObjectURL(blob);
        triggerAnchorDownload(objectUrl, fileName);
        // Отзываем object-URL после того, как браузер начал загрузку.
        setTimeout(() => URL.revokeObjectURL(objectUrl), 10000);
        return;
    }
    catch (e) {
        console.error("downloadAudio: blob download failed, fallback to anchor", e);
    }
    triggerAnchorDownload(href, fileName);
}
/** Относительный путь ручки прямой ссылки (`{ url }`). */
function downloadUrlEndpoint(audioId, kind = "full", mediaOutputId) {
    const suffix = kind === "instrumental"
        ? "instrumental/download-url"
        : kind === "wav"
            ? "wav/download-url"
            : kind === "video_cover"
                ? "video-cover/download-url"
                : kind === "karaoke"
                    ? "karaoke/download-url"
                    : "download-url";
    // media_output_id (video_cover/karaoke) — какой из накопленных результатов
    // качать. Без него ручка отдаёт последний готовый (legacy-поведение).
    const query = mediaOutputId != null ? `?media_output_id=${mediaOutputId}` : "";
    return `/api/v1/public/audios/${audioId}/${suffix}${query}`;
}
export async function downloadAudio(opts) {
    const { title, kind = "full" } = opts;
    const nameBase = kind === "instrumental"
        ? `${title || "song"} (минус)`
        : kind === "video_cover"
            ? `${title || "song"} (видео)`
            : kind === "karaoke"
                ? `${title || "song"} (караоке)`
                : title || "song";
    const ext = kind === "wav"
        ? "wav"
        : kind === "video_cover" || kind === "karaoke"
            ? "mp4"
            : "mp3";
    const fallbackName = `${sanitizeFileName(nameBase)}.${ext}`;
    // Фидбэк на время «немгновенной» операции (фетч presigned-URL + переход в
    // браузер занимает несколько секунд): показываем незакрывающийся тост
    // «Готовим файл…», снимаем его в finally — чтобы пользователь не думал, что
    // сайт завис. Один индикатор покрывает всех потребителей downloadAudio разом.
    const ru = currentLanguage() === "ru";
    const pendingToastId = toast.show({
        tone: "info",
        message: ru ? "Готовим файл к скачиванию…" : "Preparing your file…",
        duration: 0,
    });
    try {
        return await downloadAudioInner(opts, fallbackName);
    }
    finally {
        toast.dismiss(pendingToastId);
    }
}
async function downloadAudioInner(opts, fallbackName) {
    const { audioId, audioUrl, kind = "full", mediaOutputId } = opts;
    // Мини-апп + полный трек / видеообложка / караоке: «скачивание» = открыть
    // публичную страницу песни /song/<token> во внешнем браузере (та же ссылка,
    // что MAX/VK-боты шлют получателю). Теперь это хаб всех медиа (песня/видео/
    // караоке/минус/WAV) с табами — получатель качает нужное уже в НАСТОЯЩЕМ
    // браузере, а не по сырой storage-ссылке в вебвью (там mp4/mp3 редиректит/
    // проигрывает вместо сохранения). Минус/WAV на хабе тоже есть, но их чипы в
    // MySongsPage качают через presigned ниже (историческое поведение —
    // openExternalUrl отдаёт ссылку мосту). Любая ошибка (нет токена, не
    // мини-апп) — тихий фолбэк на presigned-скачивание.
    if (audioId &&
        (kind === "full" || kind === "video_cover" || kind === "karaoke") &&
        isAnyMiniApp()) {
        try {
            const token = await getAudioPublicToken(audioId);
            if (token && openExternalUrl(`${window.location.origin}/song/${token}`)) {
                return;
            }
        }
        catch (e) {
            console.error("downloadAudio: public-token failed, fallback to direct download", e);
        }
    }
    if (audioId) {
        try {
            const res = await api.get(downloadUrlEndpoint(audioId, kind, mediaOutputId));
            const url = res.data?.url;
            if (url) {
                // Мини-апп: во внешний браузер через мост (там сработает attachment).
                // Веб: якорь с download. openExternalUrl вернёт false вне мини-аппа.
                if (openExternalUrl(url))
                    return;
                await triggerWebDownload(url, fallbackName);
                return;
            }
        }
        catch (e) {
            console.error("downloadAudio: download-url failed, fallback to direct url", e);
        }
    }
    if (audioUrl) {
        if (openExternalUrl(audioUrl))
            return;
        await triggerWebDownload(audioUrl, fallbackName);
    }
}
/**
 * Скачать минус (инструментал) аудио. Минус должен быть уже готов
 * (`instrumental_status === "ready"`) — иначе ручка вернёт 404 и сработает
 * фолбэк на прямую ссылку (если передана).
 */
export async function downloadInstrumental(opts) {
    return downloadAudio({ ...opts, kind: "instrumental" });
}
/**
 * Скачать WAV-версию аудио. WAV должен быть уже готов (`wav_status === "ready"`)
 * — иначе ручка вернёт 404 и сработает фолбэк на прямую ссылку (если передана).
 * Тот же миниап-safe путь (openExternalUrl в вебвью), что и downloadInstrumental.
 */
export async function downloadWav(opts) {
    return downloadAudio({ ...opts, kind: "wav" });
}
/**
 * Скачать видеообложку (mp4) аудио. Видео должно быть уже готово
 * (`video_cover_status === "ready"`) — иначе ручка вернёт 404 и сработает
 * фолбэк на прямую ссылку (если передана). Тот же миниап-safe путь
 * (openExternalUrl в вебвью), что и downloadInstrumental/downloadWav — прямого
 * `/song/<token>` пути тут нет (видео не публикуется отдельной страницей).
 */
export async function downloadVideoCover(opts) {
    return downloadAudio({ ...opts, kind: "video_cover" });
}
/**
 * Скачать караоке-клип (mp4) аудио. Клип должен быть уже готов
 * (`karaoke_status === "ready"`) — иначе ручка вернёт 404 и сработает фолбэк на
 * прямую ссылку (если передана). Тот же миниап-safe путь (openExternalUrl в
 * вебвью), что и downloadVideoCover — прямого `/song/<token>` пути тут нет.
 */
export async function downloadKaraoke(opts) {
    return downloadAudio({ ...opts, kind: "karaoke" });
}
